
import { toast } from 'sonner';
import { generateFAQs, logFaqGenerationResult } from '@/services/faq-expert';
import { fetchArticleContent } from '@/utils/content/articleContentFetcher';
import { type CrawlResult, type EnhancedCrawlResult } from '@/types/crawler';
import { validateContent } from './validation';
import { handleFaqGeneration } from './faqGenerationHandler';
import { FAQResponse } from '@/components/faq-serp/types';

/**
 * Generate FAQs for a crawl result
 */
export const generateFaqsForCrawlResult = async (
  result: CrawlResult | EnhancedCrawlResult | null
): Promise<FAQResponse> => {
  if (!result) {
    const error = new Error("No crawl result provided");
    toast.error(error.message);
    throw error;
  }

  console.log("Starting FAQ generation for crawl result");
  const validation = validateContent(result);
  
  if (!validation.isValid) {
    const error = new Error(validation.message || "Invalid crawl result");
    toast.error(error.message);
    throw error;
  }
  
  if (!validation.pageContent || !validation.domain) {
    const error = new Error("Failed to extract page content or domain");
    toast.error(error.message);
    throw error;
  }

  // Log details of what we're sending to the API
  console.log(`Sending content (${validation.pageContent.length} chars) for domain: ${validation.domain}`);
  
  try {
    const response = await handleFaqGeneration(validation.pageContent, validation.domain);
    console.log("FAQ generation successful");
    
    // Log the successful generation
    if (response) {
      logFaqGenerationResult(response);
    } else {
      console.warn("Empty response returned from FAQ generation");
    }
    
    return response;
  } catch (error) {
    console.error("Error in generateFaqsForCrawlResult:", error);
    throw error;
  }
};

/**
 * Generate FAQs for a specific URL
 */
export const generateFaqsForUrl = async (url: string): Promise<FAQResponse> => {
  if (!url) {
    const error = new Error("No URL provided");
    toast.error(error.message);
    throw error;
  }

  console.log(`Starting FAQ generation for URL: ${url}`);
  toast.info(`Fetching content from ${url}...`);
  
  try {
    const content = await fetchArticleContent(url);
    
    if (!content || content.length < 100) {
      const errorMsg = "Not enough content found on the provided URL";
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
    
    // Extract domain from URL
    let domain;
    try {
      domain = new URL(url).hostname;
    } catch (e) {
      throw new Error(`Invalid URL format: ${url}`);
    }
    
    toast.success(`Content fetched (${content.length} chars), generating FAQs...`);
    
    // Log details of what we're sending to the API
    console.log(`Sending content (${content.length} chars) for domain: ${domain}`);
    
    const response = await handleFaqGeneration(content, domain);
    console.log("FAQ generation successful");
    
    // Log the successful generation
    if (response) {
      logFaqGenerationResult(response);
    } else {
      console.warn("Empty response returned from FAQ generation");
    }
    
    return response;
  } catch (error) {
    console.error("Error in generateFaqsForUrl:", error);
    throw error;
  }
};
